import React, { useImperativeHandle, useRef, useState } from 'react';
import { Modal, Table, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import intl from 'react-intl-universal';

import { exchangeGoods, queryExchangeGoods } from '@/api/exchangeCode';
import { fetchQueryCouponInfoByCodes } from '@/api/guest';
import { uuid } from '@/utils';
import ModalForm, { ImodalFormRef } from '../ModalForm';
import { IFieldItem } from '../CustomForm';

export interface IExchangeCodeRef {
  openModal: (data: { record: object; onOk: () => void }) => void;
}

const formAttr: Array<IFieldItem> = [
  {
    label: intl.get('cmp_exchange_code_modal_search_code_label'),
    key: 'exchangeCode',
    rules: [
      { required: true },
      {
        pattern: /^[a-zA-Z\d]+$/,
        message: intl.get('cmp_exchange_code_modal_search_code_message'),
      },
    ],
  },
];

const columns: ColumnsType<any> = [
  { title: intl.get('cmp_exchange_code_modal_column_code'), dataIndex: 'exchangeName' },
  { title: intl.get('cmp_exchange_code_modal_column_name'), dataIndex: 'goodsName' },
  { title: intl.get('cmp_exchange_code_modal_column_value'), dataIndex: 'goodsValue' },
  { title: intl.get('cmp_exchange_code_modal_column_qty'), dataIndex: 'qty' },
];

const ExchangeCodeModal = React.forwardRef((props, ref) => {
  const [userInfo, setUserInfo] = useState<any>({});

  const [exchangeCode, setExchangeCode] = useState<string>('');
  const [goodsList, setGoodsList] = useState<Array<any>>([]);
  const [goodsListLoading, setGoodsListLoading] = useState<boolean>(false);

  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [exchangeLoading, setExchangeLoading] = useState<boolean>(false);

  const paramsRef = useRef<any>();
  const inputRef = useRef<ImodalFormRef>();

  const handleOpenModal = (data) => {
    const { record = {}, onOk } = data || {};
    setUserInfo(record);
    paramsRef.current = onOk;
    inputRef.current.openModal();
  };

  const handleSave = async (values) => {
    // 小写转大写
    const { exchangeCode: code } = values;
    setExchangeCode(code.toLocaleUpperCase());
    try {
      setGoodsListLoading(true);
      const res = await queryExchangeGoods({ exchangeCode: code.toLocaleUpperCase() });
      if (res.code === 0) {
        const list = res.data || [];
        const couponCodeMap = {};
        const couponCodes = list?.map((item) => item.goodsCode);
        if (couponCodes?.length) {
          const couponRes = await fetchQueryCouponInfoByCodes(couponCodes);
          if (couponRes.code === 0) {
            couponRes.data?.forEach((item) => {
              couponCodeMap[item.couponCode] = item;
            });
          }
        }
        setGoodsList(
          list?.map((item) => ({
            ...item,
            id: uuid(),
            goodsName: couponCodeMap[item.goodsCode]?.couponName,
            goodsValue: couponCodeMap[item.goodsCode]?.couponFaceValueText,
          })),
        );
        inputRef.current.closeModal();
        setConfirmOpen(true);
      }
      setGoodsListLoading(false);
    } catch (error) {
      setGoodsListLoading(false);
    }
  };

  const handleExchange = async () => {
    try {
      setExchangeLoading(true);
      const res = await exchangeGoods({ wyuid: userInfo.wyuid, exchangeCode });
      if (res.code === 0) {
        message.success(intl.get('cmp_exchange_code_modal_message_exchange_success'));
        setConfirmOpen(false);
        if (typeof paramsRef.current === 'function') {
          paramsRef.current();
        }
      }
      setExchangeLoading(false);
    } catch (error) {
      setExchangeLoading(false);
    }
  };

  const handleCancelExchange = () => {
    setConfirmOpen(false);
  };

  useImperativeHandle(
    ref,
    () => ({
      ...inputRef.current,
      openModal: handleOpenModal,
    }),
    [],
  );

  return (
    <>
      <ModalForm
        ref={inputRef}
        title={intl.get('cmp_exchange_code_modal_title')}
        okButtonProps={{ loading: goodsListLoading }}
        formAttr={formAttr}
        onOk={handleSave}
      />
      <Modal
        title={intl.get('cmp_exchange_code_modal_confirm_title')}
        width={960}
        okText={intl.get('cmp_exchange_code_modal_confirm_btn')}
        maskClosable={false}
        open={confirmOpen}
        okButtonProps={{ loading: exchangeLoading }}
        onOk={handleExchange}
        onCancel={handleCancelExchange}
      >
        <div style={{ marginBottom: '16px' }}>
          {intl.get('cmp_exchange_code_modal_confirm_content', {
            // user: [userInfo?.name, userInfo?.certNo, userInfo?.mobile].join('，'),
            user: [userInfo?.name, userInfo?.certNo].join('，'),
          })}
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={goodsList}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Modal>
    </>
  );
});

export default ExchangeCodeModal;
